import { useMemo } from 'react';
import { compareAsc, subDays } from 'date-fns';
import { format } from 'date-fns/fp';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { keyBy } from 'lodash';
import { AlertCircle, ChartNoAxesColumn } from 'lucide-react';

import { ListFindingsThroughputPerDay } from '@/alerts/view/queries/list-findings-throughput-per-day';
import { Loading } from '@/components/ui/old/Loading';
import { useMe } from '@/hooks/use-me';
import { useNow } from '@/hooks/useNow';
import { formatEnergy } from '@/utils/format/battery';

import { ChartCardLayout } from './chart-card-layout';

const formatDateKey = format('yyyy-MM-dd');
const formatDateLabel = format('MMM d');
const DAYS_RANGE = 30;

export type ThroughputPoint = { value: number; date: Date };

export const getCompletePoints = (
  now: Date,
  delta: number,
  throughputPoints: ThroughputPoint[],
) => {
  // Get the points by the date
  const pointsPerDate = keyBy(throughputPoints, (point) =>
    formatDateKey(point.date),
  );

  return (
    Array.from({ length: delta }, (_, index) => {
      const date = subDays(now, index);
      const key = formatDateKey(date);
      const value = pointsPerDate[key]?.value ?? null;

      return { date, value };
    })
      .sort((a, b) => compareAsc(a.date, b.date))
      // TMP: temporary remove the null value
      .filter(({ value }) => value !== null)
  );
};

export function ThroughputChart() {
  const me = useMe();
  const { data, isLoading, isError } = ListFindingsThroughputPerDay.useQuery({
    daysRange: DAYS_RANGE,
  });

  const now = useNow();
  const throughputPoints = useMemo<ThroughputPoint[]>(
    () =>
      data?.map((point) => ({
        date: point.date,
        value: point.throughput,
      })) ?? [],
    [data],
  );
  const points = useMemo(
    () => getCompletePoints(now, DAYS_RANGE, throughputPoints),
    [now, throughputPoints],
  );

  return (
    <ChartCardLayout
      title="Total Throughput"
      featureFlagKey="assets"
      linkTo="/benchmark"
      buttonIcon={<ChartNoAxesColumn className="size-4" />}
      buttonLabel="View Benchmark"
    >
      <div data-testid="throughput-chart" className="w-full">
        {isLoading ? (
          <Loading />
        ) : isError || !data ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-600">
            <AlertCircle className="size-6 text-red-500" />
            <p className="text-normal font-medium">Unable to load throughput</p>
            <p className="text-sm text-gray-500">
              Please refresh the page or contact support if the issue persists.
            </p>
          </div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            className="w-full"
            options={
              {
                title: { style: { display: 'none' } },
                plotOptions: {
                  series: {
                    marker: { enabled: false },
                    animation: false,
                  },
                },
                tooltip: {
                  enabled: true,
                  formatter() {
                    return `<strong>${formatEnergy(Number(this.y), me.config.powerUnit, 0, 0)}</strong> - ${
                      this.x
                    }`;
                  },
                },
                yAxis: {
                  title: { text: '' },
                  labels: {
                    formatter() {
                      return formatEnergy(
                        Number(this.value),
                        me.config.powerUnit,
                        0,
                        2,
                      );
                    },
                  },
                },
                xAxis: {
                  categories: points.map((point) =>
                    formatDateLabel(point.date),
                  ),
                },
                series: [
                  {
                    type: 'line',
                    data: points.map((point) => point.value),
                    color: 'var(--primary-color-light)',
                  },
                ],
                chart: { renderTo: 'container', height: '50%' },
                legend: { enabled: false },
                responsive: {
                  rules: [
                    {
                      condition: {
                        maxWidth: 400,
                      },
                      chartOptions: {
                        xAxis: {
                          labels: {
                            enabled: false,
                          },
                        },
                        yAxis: {
                          labels: {
                            enabled: false,
                          },
                        },
                      },
                    },
                  ],
                },
                credits: { enabled: false },
              } as Highcharts.Options
            }
          />
        )}
      </div>
    </ChartCardLayout>
  );
}
